import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { quotesApi, suppliersApi } from '../../../services/api'
import { getErrorMessage } from '../../../utils/errors'
import TableActionButton from '../../../components/TableActionButton'
import QuoteStatusBadge from './QuoteStatusBadge'
import { useSortableData } from '../../../hooks/useSortableData'
import SortableTh from '../../../components/SortableTh'

interface Supplier {
  id: number
  name: string
  is_active: boolean
}

interface Quote {
  id: number
  supplier_id: number
  supplier_name: string
  item_description: string
  amount: number
  quote_date: string | null
  status: string
  notes: string | null
  created_by: number | null
  created_at: string
  updated_at: string
}

interface QuoteForm {
  supplier_id: string
  item_description: string
  amount: string
  quote_date: string
  status: 'pending' | 'accepted' | 'rejected'
  notes: string
}

const emptyForm: QuoteForm = {
  supplier_id: '',
  item_description: '',
  amount: '',
  quote_date: '',
  status: 'pending',
  notes: '',
}

export default function QuotesTab() {
  const [quotes, setQuotes] = useState<Quote[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null)
  const [form, setForm] = useState<QuoteForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchAll = async () => {
    try {
      setLoadError('')
      const [quotesRes, suppliersRes] = await Promise.all([quotesApi.list(), suppliersApi.list()])
      setQuotes(quotesRes.data.quotes || [])
      setSuppliers(suppliersRes.data.suppliers || [])
    } catch (err) {
      console.error('Error fetching quotes:', err)
      setLoadError('No se pudieron cargar las cotizaciones. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeSuppliers = useMemo(() => suppliers.filter((s) => s.is_active), [suppliers])

  const { sortedData: sortedQuotes, sortConfig, requestSort } = useSortableData(quotes)

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingQuote(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (q: Quote) => {
    setForm({
      supplier_id: String(q.supplier_id),
      item_description: q.item_description,
      amount: String(q.amount),
      quote_date: q.quote_date || '',
      status: (q.status as QuoteForm['status']) || 'pending',
      notes: q.notes || '',
    })
    setEditingQuote(q)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingQuote && !form.supplier_id) {
      setError('Selecciona un proveedor')
      return
    }
    if (!form.item_description.trim()) {
      setError('Ingresa la descripción del ítem')
      return
    }
    if (form.amount === '' || parseFloat(form.amount) < 0) {
      setError('Ingresa un monto válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (editingQuote) {
        await quotesApi.update(editingQuote.id, {
          item_description: form.item_description.trim(),
          amount: parseFloat(form.amount),
          quote_date: form.quote_date || null,
          notes: form.notes.trim() || null,
          status: form.status,
        })
        toast.success('Cotización actualizada')
      } else {
        await quotesApi.create({
          supplier_id: Number(form.supplier_id),
          item_description: form.item_description.trim(),
          amount: parseFloat(form.amount),
          quote_date: form.quote_date || undefined,
          notes: form.notes.trim() || undefined,
        })
        toast.success('Cotización creada')
      }
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(getErrorMessage(err, 'Error al guardar la cotización'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Cotizaciones recibidas de proveedores. Al crear una cotización queda en estado Pendiente; edítala para
          marcarla como Aceptada o Rechazada.
        </p>
        <button
          onClick={openNew}
          disabled={activeSuppliers.length === 0}
          title={activeSuppliers.length === 0 ? 'Crea primero un proveedor activo en la pestaña Proveedores' : undefined}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nueva Cotización
        </button>
      </div>

      {activeSuppliers.length === 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          No hay proveedores activos. Crea uno en la pestaña "Proveedores" antes de registrar una cotización.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && quotes.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay cotizaciones registradas aún.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Proveedor" sortKey="supplier_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Ítem" sortKey="item_description" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Monto" sortKey="amount" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Fecha" sortKey="quote_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedQuotes.map((q) => (
                  <tr key={q.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{q.supplier_name}</td>
                    <td className="px-4 py-4 text-sm text-espresso-600">{q.item_description}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">${Number(q.amount).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {q.quote_date ? new Date(q.quote_date).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap"><QuoteStatusBadge status={q.status} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <TableActionButton onClick={() => openEdit(q)} variant="primary">
                        Editar
                      </TableActionButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingQuote ? 'Editar Cotización' : 'Nueva Cotización'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="quote-supplier" className="block text-sm font-medium text-espresso-700 mb-1">
                  Proveedor *
                </label>
                <select
                  id="quote-supplier"
                  name="supplier_id"
                  value={form.supplier_id}
                  onChange={handleChange}
                  disabled={!!editingQuote}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                  required
                >
                  <option value="">Selecciona un proveedor</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id} disabled={!s.is_active}>
                      {s.name}{!s.is_active ? ' (inactivo)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="quote-item" className="block text-sm font-medium text-espresso-700 mb-1">
                  Descripción del ítem *
                </label>
                <input
                  id="quote-item"
                  type="text"
                  name="item_description"
                  value={form.item_description}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="quote-amount" className="block text-sm font-medium text-espresso-700 mb-1">
                    Monto *
                  </label>
                  <input
                    id="quote-amount"
                    type="number"
                    name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="quote-date" className="block text-sm font-medium text-espresso-700 mb-1">
                    Fecha de cotización
                  </label>
                  <input
                    id="quote-date"
                    type="date"
                    name="quote_date"
                    value={form.quote_date}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
              </div>
              {editingQuote && (
                <div>
                  <label htmlFor="quote-status" className="block text-sm font-medium text-espresso-700 mb-1">
                    Estado
                  </label>
                  <select
                    id="quote-status"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="accepted">Aceptada</option>
                    <option value="rejected">Rechazada</option>
                  </select>
                </div>
              )}
              <div>
                <label htmlFor="quote-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="quote-notes"
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingQuote ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
